import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import type { Card as CardType, ColumnId, SortState } from '../data/types';
import { COLUMNS, SORT_COLUMNS } from '../data/types';
import { Column } from './Column';
import { Card } from './Card';
import styles from './Sorter.module.css';

interface SorterProps {
  deck: CardType[];
  sort: SortState;
  onSortChange: (next: SortState) => void;
  onFinish: () => void;
}

export function Sorter({ deck, sort, onSortChange, onFinish }: SorterProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [keyboardPickPrompt, setKeyboardPickPrompt] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor)
  );

  function handleDragStart(ev: DragStartEvent) {
    setActiveId(String(ev.active.id));
  }

  function handleDragEnd(ev: DragEndEvent) {
    setActiveId(null);
    if (!ev.over) return;
    const cardId = String(ev.active.id);
    const target = String(ev.over.id) as ColumnId;
    if (sort[cardId] === target) return;
    onSortChange({ ...sort, [cardId]: target });
  }

  function move(cardId: string, target: ColumnId) {
    onSortChange({ ...sort, [cardId]: target });
  }

  const byColumn = COLUMNS.reduce<Record<ColumnId, CardType[]>>(
    (acc, col) => {
      acc[col.id] = deck.filter((c) => sort[c.id] === col.id);
      return acc;
    },
    { deck: [], hard: [], soft: [], curious: [], yes: [], hellyes: [] }
  );

  const totalSorted = deck.length - byColumn.deck.length;
  const totalCards = deck.length;
  const topOfDeck = byColumn.deck[0];

  const activeCard = activeId ? deck.find((c) => c.id === activeId) : null;

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <header className={styles.progress}>
        <span className={styles.progressLabel}>
          {totalSorted} of {totalCards} sorted
        </span>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ transform: `scaleX(${totalCards > 0 ? totalSorted / totalCards : 0})` }}
          />
        </div>
        <button
          type="button"
          className={styles.finishBtn}
          onClick={onFinish}
          disabled={totalSorted === 0}
        >
          {totalSorted === totalCards ? 'see the portrait →' : 'finish where you are →'}
        </button>
      </header>

      <div className={styles.grid}>
        {/* Deck column shows the next card up */}
        <Column
          column={COLUMNS[0]!}
          cards={byColumn.deck}
          isDeck
          onPickColumnForTop={topOfDeck ? () => setKeyboardPickPrompt(true) : undefined}
        />

        {/* Five sort columns */}
        {SORT_COLUMNS.map((col) => (
          <Column key={col.id} column={col} cards={byColumn[col.id]} />
        ))}
      </div>

      {/* Modal for picking a column via tap, for the current top card */}
      {keyboardPickPrompt && topOfDeck && (
        <div
          className={styles.pickModal}
          onClick={(e) => {
            if (e.target === e.currentTarget) setKeyboardPickPrompt(false);
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className={styles.pickInner}>
            <p className={styles.pickHeading}>
              place <em>{topOfDeck.name}</em> into:
            </p>
            <div className={styles.pickButtons}>
              {SORT_COLUMNS.map((col) => (
                <button
                  key={col.id}
                  type="button"
                  className={styles.pickColBtn}
                  style={{ borderLeftColor: col.accent }}
                  onClick={() => {
                    move(topOfDeck.id, col.id);
                    setKeyboardPickPrompt(false);
                  }}
                >
                  {col.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={styles.pickCancel}
              onClick={() => setKeyboardPickPrompt(false)}
            >
              cancel
            </button>
          </div>
        </div>
      )}

      <DragOverlay>
        {activeCard ? <Card card={activeCard} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
